// ============================================================================
// HardwareLoad — per-leg latency comparison across tiers (PRD §1.9-B, D5).
// ============================================================================
// Leads with the REAL per-leg latency the fleet already collects, labeled by
// tier (≈$150 mini-PC → gaming rig). NO paid cloud demo machines in beta
// (locked decision 4) — this is collected-numbers, not a live rig. The numbers
// today are representative placeholders in latency.ts; wire the real medians
// there (the device records stage1_ms/stage2_ms per detection).
// ============================================================================

import { TIER_LATENCY, fmtMs, type LatencyLeg } from "./latency";

// The widest single leg across all tiers, to scale the inline bar widths.
const MAX_MS = Math.max(
  ...TIER_LATENCY.flatMap((t) => t.legs.map((l) => l.ms)),
);

function tierLabel(tier: string): string {
  return tier === "lite" ? "Lite" : tier === "standard" ? "Standard" : "Max";
}

function legTotal(legs: LatencyLeg[]): number {
  // The "scan a frame" cost = every-frame legs (the describer only runs on a
  // flagged frame, so it's not part of the steady-state per-frame cost).
  return legs
    .filter((l) => l.cadence === "every-frame")
    .reduce((sum, l) => sum + l.ms, 0);
}

export function HardwareLoad() {
  return (
    <div>
      <div className="callout">
        <strong>Real collected numbers, not a marketing rig.</strong> These are
        representative per-leg timings from actual machines across the tiers.
        The fast gatekeeper runs on <em>every</em> frame; the accurate describer
        runs <em>only</em> on a flagged frame — which is why a weak PC still
        feels responsive day-to-day and only the rare flagged frame is slow.
      </div>

      {TIER_LATENCY.map((t) => (
        <section key={t.tier} style={{ marginTop: 28 }}>
          <h3 style={{ marginBottom: 4 }}>
            {tierLabel(t.tier)}{" "}
            <span className="mono muted">· {t.machine}</span>
          </h3>
          <table className="load-table" style={{ marginTop: 12 }}>
            <thead>
              <tr>
                <th>Pipeline leg</th>
                <th style={{ textAlign: "right" }}>Time</th>
                <th>Relative</th>
              </tr>
            </thead>
            <tbody>
              {t.legs.map((l) => (
                <tr key={l.key}>
                  <td>
                    <span className="leg-name">{l.label}</span>
                    <span className="cadence">
                      {l.cadence === "every-frame"
                        ? "runs on every frame"
                        : "runs only on a flagged frame"}
                    </span>
                  </td>
                  <td className="num" data-label="Time">
                    {fmtMs(l.ms)}
                  </td>
                  <td data-label="Relative">
                    <span
                      className={"bar" + (l.key === "vlm" ? " vlm" : "")}
                      style={{
                        width: `${Math.max(2, (l.ms / MAX_MS) * 160)}px`,
                      }}
                    />
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td>Steady-state cost to scan a frame</td>
                <td className="num" data-label="Time">
                  {fmtMs(legTotal(t.legs))}
                </td>
                <td />
              </tr>
            </tbody>
          </table>
        </section>
      ))}

      <p className="mono muted" style={{ marginTop: 24, fontSize: "0.72rem" }}>
        Moat note: for a customer device the &ldquo;upload&rdquo; leg carries
        only a heartbeat — no screenshots, no descriptions ever leave the
        machine. The figure shown is the tenant-zero development lane.
      </p>
    </div>
  );
}
